"use client"

import * as React from "react"
import { Calendar as CalendarIcon } from "lucide-react"
import { format, parse, isValid, subYears } from "date-fns"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface EnhancedDatePickerProps {
  /** The selected date value */
  value?: Date | null
  /** Callback when date changes */
  onValueChange?: (date: Date | null) => void
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether the input is read-only */
  readOnly?: boolean
  /** Placeholder text for the input */
  placeholder?: string
  /** Additional CSS classes */
  className?: string
  /** ID for the input element */
  id?: string
  /** ARIA label for accessibility */
  ariaLabel?: string
}

export function EnhancedDatePicker({
  value,
  onValueChange,
  disabled = false,
  readOnly = false,
  placeholder = "M/d/yy",
  className,
  id,
  ariaLabel = "Date picker"
}: EnhancedDatePickerProps) {
  const [inputValue, setInputValue] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [calendarMonth, setCalendarMonth] = React.useState<Date>()

  // Initialize input value and calendar month when value changes
  React.useEffect(() => {
    if (value) {
      // Format as M/d/yyyy for more natural display (e.g., 5/19/2019 instead of 05/19/2019)
      setInputValue(format(value, 'M/d/yyyy'))
      setCalendarMonth(value)
      setError(null)
    } else {
      setInputValue('')
      setError(null)
    }
  }, [value])

  // Parse and validate date input with multiple flexible formats
  const parseInputDate = React.useCallback((input: string): Date | null => {
    if (!input.trim()) return null
    
    // First, normalize 2-digit years to 4-digit years
    const normalizeYear = (dateStr: string): string => {
      // Match patterns like M/d/yy, MM/dd/yy, etc.
      const twoDigitYearPattern = /(\d{1,2}\/\d{1,2}\/)(\d{2})$/
      const match = dateStr.match(twoDigitYearPattern)
      
      if (match) {
        const yearPart = parseInt(match[2], 10)
        // Apply century logic: 00-29 → 2000-2029, 30-99 → 1930-1999
        const fullYear = yearPart <= 29 ? yearPart + 2000 : yearPart + 1900
        return match[1] + fullYear.toString()
      }
      
      return dateStr
    }
    
    const normalizedInput = normalizeYear(input)
    
    // Try multiple date formats in order of preference
    const formats = [
      'MM/dd/yyyy',  // 05/19/2019
      'M/d/yyyy',    // 5/1/2019
      'M/dd/yyyy',   // 5/19/2019
      'MM/d/yyyy',   // 05/1/2019
    ]
    
    for (const formatPattern of formats) {
      try {
        const parsed = parse(normalizedInput, formatPattern, new Date())
        if (isValid(parsed)) {
          return parsed
        }
      } catch {
        // Continue to next format
        continue
      }
    }
    
    return null
  }, [])

  // Handle text input changes with no auto-population while typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    
    // Clear any existing errors when user starts typing
    setError(null)
    
    // If user clears the input, update the value immediately
    if (!newValue.trim()) {
      onValueChange?.(null)
      return
    }
    
    // Don't auto-parse or auto-populate while typing - let user type freely
    // Parsing and validation will happen on blur
  }

  // Handle calendar date selection
  const handleCalendarSelect = (date: Date | undefined) => {
    if (date) {
      setInputValue(format(date, 'M/d/yyyy'))
      setError(null)
      onValueChange?.(date)
      setIsOpen(false) // Close calendar immediately after selection
    }
  }

  // Handle input blur for validation and parsing
  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      setIsOpen(false)
      return
    }
    
    const parsedDate = parseInputDate(inputValue)
    if (parsedDate) {
      // Valid date - update the value and calendar
      setError(null)
      setCalendarMonth(parsedDate)
      onValueChange?.(parsedDate)
    } else {
      // Invalid date - show error
      setError('Please enter a valid date (e.g., 5/19/19 or 05/19/2019)')
    }
    setIsOpen(false)
  }

  const isFieldDisabled = disabled || readOnly
  const today = new Date()

  return (
    <div className={cn("relative", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <CalendarIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-75 pointer-events-none" />
            <Input
              id={id}
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={() => !isFieldDisabled && setIsOpen(true)}
              placeholder={placeholder}
              disabled={isFieldDisabled}
              readOnly={readOnly}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className={cn(
                "pl-8 pr-3 w-32", // Sized for date content: left padding for icon, right padding, compact width for ~8-9 chars
                error && "border-red-500 focus:border-red-500",
                "focus-visible:border-primary"
              )}
              aria-label={ariaLabel}
              aria-invalid={!!error}
              aria-describedby={error ? `${id}-error` : undefined}
            />
          </div>
        </PopoverTrigger>
        
        {error && (
          <p 
            id={`${id}-error`}
            className="text-sm text-red-500 mt-1"
            role="alert"
          >
            {error}
          </p>
        )}

        <PopoverContent className="w-auto p-0 bg-background text-popover-foreground" align="start">
          <Calendar
            mode="single"
            selected={value || undefined}
            onSelect={handleCalendarSelect}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            disabled={{ after: today }}
            captionLayout="label"
            navLayout="around"
            startMonth={subYears(today, 10)}
            endMonth={today}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

EnhancedDatePicker.displayName = "EnhancedDatePicker" 